import { DashboardImpersonationControllerImpersonate403AllOfErrorCode } from "@/api/__generated__/schemas";
import { LoginApp } from "@/api/__generated__/schemas/loginApp";
import type { MessageKey } from "@/i18n";

/**
 * Display order for the app picker. Every `LoginApp` member is listed, so the
 * picker offers exactly the intersection of this list with the apps the backend
 * authorised — never an app it invented. When the API gains a new app, add it
 * here **and** give it an origin + landing path in `ImpersonateUserModal`,
 * otherwise it silently stays unofferable.
 */
const IMPERSONATION_APP_ORDER: readonly LoginApp[] = [
  LoginApp.dashboard,
  LoginApp.manager,
  LoginApp.client,
];

/**
 * The apps the picker may offer for a target user: the backend's
 * `allowed-apps` response, ordered for display. Fails closed — while the
 * response is unresolved (`null` / `undefined`) nothing is offered.
 *
 * The minted token is bound to the requested app by an `aud` claim, so an app
 * the backend did not authorise must never reach the picker: whatever is
 * selected here is both what the POST asks for and the surface the new tab
 * opens on.
 */
export const resolveAvailableImpersonationApps = (
  availableApps: readonly LoginApp[] | null | undefined
): readonly LoginApp[] => {
  const allowedApps = new Set(availableApps ?? []);
  return IMPERSONATION_APP_ORDER.filter((app) => allowedApps.has(app));
};

/* ===========================================================================
 * Impersonation refusal codes → operator-facing copy.
 *
 * THIS TABLE IS THE ONLY PLACE A NEW REFUSAL REASON HAS TO BE ADDED: one row
 * per backend `error.code`, keyed by the wire string. Anything unmapped (or a
 * response with no code at all) falls back to the generic
 * `IMPERSONATE_UNKNOWN_ERROR_KEY` toast, so an operator only ever sees "could
 * not start the session" for failures we have no better answer for.
 *
 * Codes are referenced through the generated per-endpoint enum wherever
 * codegen already knows them. The DEV-4492 refusals below are not in
 * `DashboardImpersonationControllerImpersonate403AllOfErrorCode` yet (they ship
 * with the backend's OpenAPI schema); swap each string literal for its
 * generated enum member on the next `yarn rest:codegen`.
 * ======================================================================== */
export const IMPERSONATE_ERROR_MESSAGE_KEYS: Record<string, MessageKey> = {
  /** The target user cannot sign in to the requested app. */
  [DashboardImpersonationControllerImpersonate403AllOfErrorCode.IMPERSONATION_TARGET_NO_APP_ACCESS]:
    "user.impersonation.errorState.noAccess",
  /** The caller is not a superAdmin — impersonation is superAdmin-only. */
  IMPERSONATION_CALLER_NOT_SUPER_ADMIN: "user.impersonation.errorState.notSuperAdmin",
  /** The caller's own session is already impersonated — no chaining. */
  IMPERSONATION_CHAINING_FORBIDDEN: "user.impersonation.errorState.chainingForbidden",
  // Deliberately absent: a superAdmin target, self-impersonation and a missing
  // `USER:IMPERSONATE` capability all come back as the generic `FORBIDDEN`
  // code, distinguishable only by the untranslated backend message. They stay
  // on the generic toast rather than being discriminated by message text.
};

const IMPERSONATE_UNKNOWN_ERROR_KEY: MessageKey = "user.impersonation.errorState.failed";

/**
 * Resolve an impersonation API error code to its translation key, falling back
 * to the generic failure message for unmapped or missing codes.
 */
export const resolveImpersonateErrorMessageKey = (code: string | null | undefined): MessageKey =>
  (code ? IMPERSONATE_ERROR_MESSAGE_KEYS[code] : undefined) ?? IMPERSONATE_UNKNOWN_ERROR_KEY;
